import { useEffect, useState } from "react";
import { X, Send, Loader2, Check, Phone, Mail, User, MapPin, MessageSquare, Plus, Minus, ShoppingBag } from "lucide-react";
import { submitConsumerRequest } from "../lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { createPortal } from "react-dom";

/**
 * RequestModal — Consumer aanvraag modal.
 * Opens with a preselected product+variant. Users can adjust quantity, fill in
 * their contact details and submit. On success, shows a confirmation state.
 */
const RequestModal = ({ open, onClose, product, variant }) => {
  const [qty, setQty] = useState(1);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    postal_code: "",
    city: "",
    message: "",
  });
  const [ageOk, setAgeOk] = useState(false);
  const [termsOk, setTermsOk] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setQty(1);
      setDone(false);
    }
  }, [open, product?.id, variant?.id]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open || !product) return null;

  const accent = product.accent_color || "#ff1e1e";

  const submit = async (e) => {
    e.preventDefault();
    if (!ageOk) {
      toast.error("Je moet 18+ zijn om een aanvraag te doen.");
      return;
    }
    if (!termsOk) {
      toast.error("Ga akkoord met de voorwaarden.");
      return;
    }
    setLoading(true);
    try {
      await submitConsumerRequest({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        postal_code: form.postal_code.trim(),
        city: form.city.trim() || undefined,
        items: [
          {
            product_id: product.id,
            product_name: product.name,
            variant_label: variant?.label,
            quantity: qty,
          },
        ],
        message: form.message.trim() || undefined,
        age_confirmed: ageOk,
        terms_accepted: termsOk,
      });
      setDone(true);
      toast.success("Aanvraag verstuurd — check je inbox!");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Aanvraag versturen mislukt. Probeer het later opnieuw.";
      toast.error(typeof msg === "string" ? msg : "Aanvraag versturen mislukt.");
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div
      data-testid="consumer-request-modal"
      className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center p-4 sm:p-8 overflow-y-auto"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl bg-[#0a0a0a] border border-[var(--tha-border)] my-8"
        onClick={(e) => e.stopPropagation()}
        style={{ boxShadow: `0 0 0 1px ${accent}66, 0 0 40px ${accent}22` }}
      >
        <div className="flex items-center justify-between p-6 border-b border-[var(--tha-border)]">
          <div>
            <div className="text-[10px] font-mono-x tracking-[0.3em]" style={{ color: accent }}>
              AANVRAAG
            </div>
            <div className="font-display text-3xl sm:text-4xl mt-1 leading-none">
              BESTELLING <span style={{ color: accent }}>AANVRAGEN</span>
            </div>
          </div>
          <button
            data-testid="request-modal-close"
            onClick={onClose}
            className="p-2 hover:bg-white/5 text-white"
            aria-label="Sluiten"
          >
            <X size={22} />
          </button>
        </div>

        {done ? (
          <div data-testid="request-success" className="p-10 text-center">
            <div
              className="inline-flex w-16 h-16 items-center justify-center border-2"
              style={{ borderColor: accent, color: accent }}
            >
              <Check size={30} />
            </div>
            <div className="font-display text-4xl mt-6 leading-none">
              TIK, <span style={{ color: accent }}>BINNEN.</span>
            </div>
            <p className="mt-4 text-[var(--tha-muted)] max-w-md mx-auto">
              We hebben je aanvraag ontvangen en tikken je <strong className="text-white">binnen 1 werkdag</strong> aan
              met een bevestiging en de details.
            </p>
            <button
              data-testid="request-modal-done"
              onClick={onClose}
              className="mt-8 px-8 py-3 font-display tracking-wider inline-flex items-center gap-2"
              style={{ background: accent, color: "#050505" }}
            >
              SLUITEN
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 sm:p-8">
            {/* Product summary */}
            <div className="flex items-center gap-4 p-4 border border-[var(--tha-border)] bg-[#050505] mb-6">
              {product.image && (
                <img
                  src={product.image}
                  alt=""
                  className="w-16 h-16 object-cover border border-[var(--tha-border)]"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg text-white truncate">
                  {product.name.toUpperCase()}
                </div>
                {variant && (
                  <div className="text-xs font-mono-x text-[var(--tha-muted)] mt-1">
                    {variant.label}
                  </div>
                )}
              </div>
              <div className="flex items-center border border-[var(--tha-border)]">
                <button
                  type="button"
                  data-testid="request-modal-qty-minus"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="p-2 hover:bg-white/5 text-white"
                  aria-label="Min"
                >
                  <Minus size={14} />
                </button>
                <span className="px-4 font-mono-x text-sm min-w-[36px] text-center">{qty}</span>
                <button
                  type="button"
                  data-testid="request-modal-qty-plus"
                  onClick={() => setQty((q) => q + 1)}
                  className="p-2 hover:bg-white/5 text-white"
                  aria-label="Plus"
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field icon={User} label="Naam" required>
                <input
                  data-testid="request-modal-name"
                  type="text"
                  required
                  autoComplete="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-transparent border-none outline-none text-white placeholder-[var(--tha-muted)]"
                  placeholder="Jouw naam"
                />
              </Field>
              <Field icon={Mail} label="E-mail" required>
                <input
                  data-testid="request-modal-email"
                  type="email"
                  required
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-transparent border-none outline-none text-white placeholder-[var(--tha-muted)]"
                  placeholder="jouw@email.nl"
                />
              </Field>
              <Field icon={Phone} label="Telefoon" required>
                <input
                  data-testid="request-modal-phone"
                  type="tel"
                  required
                  autoComplete="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full bg-transparent border-none outline-none text-white placeholder-[var(--tha-muted)]"
                  placeholder="06 12 34 56 78"
                />
              </Field>
              <Field icon={MapPin} label="Postcode" required>
                <input
                  data-testid="request-modal-postal"
                  type="text"
                  required
                  autoComplete="postal-code"
                  value={form.postal_code}
                  onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                  className="w-full bg-transparent border-none outline-none text-white placeholder-[var(--tha-muted)] uppercase"
                  placeholder="1234 AB"
                />
              </Field>
              <div className="sm:col-span-2">
                <Field icon={MessageSquare} label="Bericht (optioneel)">
                  <textarea
                    data-testid="request-modal-message"
                    rows={3}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full bg-transparent border-none outline-none text-white placeholder-[var(--tha-muted)] resize-none"
                    placeholder="Wensen, event, aflevermoment..."
                  />
                </Field>
              </div>
            </div>

            <div className="mt-6 space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  data-testid="request-modal-age"
                  type="checkbox"
                  checked={ageOk}
                  onChange={(e) => setAgeOk(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[var(--tha-red)]"
                />
                <span className="text-sm text-[var(--tha-muted)]">
                  Ik bevestig dat ik <strong className="text-white">18 jaar of ouder</strong> ben.
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  data-testid="request-modal-terms"
                  type="checkbox"
                  checked={termsOk}
                  onChange={(e) => setTermsOk(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[var(--tha-red)]"
                />
                <span className="text-sm text-[var(--tha-muted)]">
                  Ik ga akkoord met de{" "}
                  <Link to="/voorwaarden" target="_blank" className="text-white underline">
                    algemene voorwaarden
                  </Link>{" "}
                  en het{" "}
                  <Link to="/privacy" target="_blank" className="text-white underline">
                    privacybeleid
                  </Link>
                  .
                </span>
              </label>
            </div>

            <button
              data-testid="request-modal-submit"
              type="submit"
              disabled={loading}
              className="mt-8 w-full py-4 font-display tracking-wider inline-flex items-center justify-center gap-3 disabled:opacity-60"
              style={{ background: accent, color: "#050505" }}
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {loading ? "VERZENDEN..." : "VERSTUUR AANVRAAG"}
            </button>
            <p className="mt-4 text-center text-xs text-[var(--tha-muted)] font-mono-x">
              Geen betaling nu. We nemen contact op met bevestiging + prijsopgave.
            </p>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
};

const Field = ({ icon: Icon, label, required, children }) => (
  <label className="flex flex-col gap-1.5 p-3 border border-[var(--tha-border)] bg-[#050505] focus-within:border-white transition-colors">
    <span className="text-[10px] font-mono-x tracking-[0.25em] text-[var(--tha-muted)] inline-flex items-center gap-2">
      <Icon size={11} /> {label}
      {required && <span className="text-[var(--tha-red)]">*</span>}
    </span>
    {children}
  </label>
);

export default RequestModal;
